

//warning!! this file is also shared and used inside demo page, see uniwizard-lib.js
//be careful when changing
import { getTranslator } from '../js/i18n';
import { cmpVersions, useNephalemWacomApi } from './plugin_api';
import utils from './utils';

const t = getTranslator();

const PacketState = {
    nWEII_STATE_RANGED: 4,
    nWEII_STATE_TOUCHED: 13,
    nWEII_OK_BIT: 32,
    nWEII_REPEAT_BIT: 64,
    nWEII_CANCEL_BIT: 128,
};

//currently extension doesn't support buttons, so we use mouse handler to process buttons
export default function WacomExtHandler(signer, _guiHelper) {
    let api = null;
    let usbDevice = {};
    let userEnvironment = {};
    let idProduct = '';
    let penData = [];
    let wndData = {};
    let timer;
    let isCapturing = false;
    let wacomScreenButtonPressed = false;
    let transform = {};
    let penColor = '#0000B4';
    let averageThickness = 1.5;

    this.init = function(options = {}) {
        api = useNephalemWacomApi({ guiHelper: _guiHelper });
        return Promise.resolve()
            .then(() => api.init())
            .then(() => api.send('GetCaptureCaps'))
            .then((caps) => {
                if(!caps?.devices?.length || !caps?.devices?.[0]?.name) {
                    const err = api.pluginDiagnostic.markFailed(undefined, t('wacom.device_not_available'));
                    return Promise.reject(err);
                }
                const _usbDevice = caps.devices[0];
                idProduct = _usbDevice.name;
                api.pluginDiagnostic.setCurrentVersion('info_wacom', idProduct);
                const requiredPluginVersion = api.supportedVersions[idProduct];
                if(!requiredPluginVersion) {
                    const err = api.pluginDiagnostic.markFailed(
                        'info_wacom',
                        undefined,
                        t('wacom.device_not_supported', { devices: Object.keys(api.supportedVersions).join(', ') }),
                    );
                    return Promise.reject(err);
                }
                const currentPluginVersion = api.pluginDiagnostic.getCurrentVersion('info_plugin');
                if(currentPluginVersion && (cmpVersions(currentPluginVersion, requiredPluginVersion) < 0)) {
                    const err = api.markTabletPluginErrorDetails(requiredPluginVersion);
                    return Promise.reject(err);
                }
                usbDevice = {
                    screenWidth: _usbDevice.displayW,
                    screenHeight: _usbDevice.displayH,
                    offsetx: _usbDevice.displayOffsetX,
                    offsety: _usbDevice.displayOffsetY,
                    tabletMaxX: _usbDevice.packetMaxX,
                    tabletMaxY: _usbDevice.packetMaxY,
                    tabletMaxPressure: _usbDevice.packetMaxPressure,
                };
                userEnvironment = {
                    nephalemPluginVersion: currentPluginVersion,
                    nephalemExtensionVersion: api.pluginDiagnostic.getCurrentVersion('info_extension'),
                    browserVersion: api.pluginDiagnostic.getCurrentVersion('info_browser'),
                };
                penColor = options.penColor;
                averageThickness = options.averageThickness;
                return undefined;
            })
            .catch((err) => {
                api.shutdown();
                return Promise.reject(err);
            });
    };

    this.connect = function() {
        //SetCaptureCaps
        //penThickness -- 0, 1, 2, 3 (1 - default)
        //heartbeat -- timeout (0 off - for debugging, 500 default)

        //our default preview thickness = (2.5 - 0.5) / 2 + 0.5 = 1.5
        let penThickness = 0;
        if((averageThickness >= 1) && (averageThickness < 2)) {
            penThickness = 1;
        } else if((averageThickness >= 2) && (averageThickness < 6)) {
            penThickness = 2;
        } else if(averageThickness >= 6) {
            penThickness = 3;
        }

        return Promise.resolve()
            .then(() => api.send('SetCaptureCaps', { device: idProduct, configString: '', penColor, penThickness }))
            .then(() => api.send('StartCapture'))
            .then(() => {
                penData = [];
                isCapturing = true;

                // Create the signature window.
                // The STU-300 is very shallow, so it is better to utilise
                // the buttons to the side of the display instead.
                wndData = signer.createModalWindow(idProduct != 'STU-300');
                signer.selectDevice(this, utils.FILTER_WACOM_EXT);

                const { width, height } = wndData;
                const { tabletMaxX, tabletMaxY } = usbDevice;
                //determine max aspect difference between screen rect and device rect
                //then put real image on the center of screen rect reduced to screen aspect
                //see it.comped.jdigitalsign.crypt2.sign.pades.PDFView.addImage
                const dI = tabletMaxX / tabletMaxY;
                let aspect;
                let dx;
                let dy;
                let offsetx = 0;
                let offsety = 0;
                if((dI * height) > width) {
                    dx = width;
                    dy = width / dI;
                    offsety += (height - dy) / 2;
                    aspect = width / tabletMaxX;
                } else {
                    dx = height * dI;
                    dy = height;
                    offsetx += (width - dx) / 2;
                    aspect = height / tabletMaxY;
                }

                transform = {
                    aspect,
                    dx,
                    dy,
                    offsetx,
                    offsety,
                };
                continueCapthure();
            });
    };

    function continueCapthure() {
        timer = setTimeout(() => {
            Promise.resolve()
                .then(() => api.send('GetCapture'))
                .then((data) => {
                    let shouldContinue = true;
                    if(data?.packets?.length) {
                        shouldContinue = pollTablet(data.packets);
                    }
                    if(shouldContinue) {
                        continueCapthure();
                    } else {
                        timer = undefined;
                    }
                })
                .catch((err) => {
                    if(typeof (console?.log) == 'function') {
                        console.log(err?.message);
                    }
                    continueCapthure();
                });
        }, 100);
    }

    function tabletToWindow(penDataObj) {
        // Screen means LCD screen of the tablet.
        const screenx = (penDataObj.x * transform.aspect) + transform.offsetx;
        const screeny = (penDataObj.y * transform.aspect) + transform.offsety;
        return new utils.Point(screenx, screeny);
    }

    function pollTablet(packets) {
        let buttonClick;
        for(const packet of packets) {
            const state = packet.stato;
            if(state == (PacketState.nWEII_STATE_RANGED + PacketState.nWEII_OK_BIT)) {
                const okButton = wndData.btns[0];
                buttonClick = okButton;
                break;
            }
            if(state == (PacketState.nWEII_STATE_RANGED + PacketState.nWEII_REPEAT_BIT)) {
                const repeatButton = wndData.btns[1];
                buttonClick = repeatButton;
                break;
            }
            if(state == (PacketState.nWEII_STATE_RANGED + PacketState.nWEII_CANCEL_BIT)) {
                const cancelButton = wndData.btns[2];
                buttonClick = cancelButton;
                break;
            }

            const isDown = state == PacketState.nWEII_STATE_TOUCHED;
            if(!isDown && !penData.length) {
                continue;
            }
            //we pressed pen outside button, let's capture
            //also capture pen up events, when we already captured some pen downs, as C# BioSign does
            const penDataObj = {
                x: packet.x,
                y: packet.y,
                pressure: packet.prssr,
                time: packet.timestamp,
                isDown,
            };
            const prevPenData = penData.length ? penData[penData.length - 1] : undefined;
            const wasDown = prevPenData?.isDown ?? false;
            penData.push(penDataObj);

            if(isDown && wasDown) {
                signer.onPenData(
                    tabletToWindow(prevPenData),
                    tabletToWindow(penDataObj),
                    penDataObj.pressure / usbDevice.tabletMaxPressure,
                );
            }
        }

        if(buttonClick) {
            wacomScreenButtonPressed = true;
            setTimeout(() => buttonClick.click(), 10);
            return false;
        }
        return true;
    }

    function stopCapture() {
        return Promise.resolve()
            .then(() => {
                if(timer) {
                    clearTimeout(timer);
                    timer = undefined;
                }
            })
            .then(() => {
                if(isCapturing) {
                    isCapturing = false;
                    return api.send('StopCapture');
                }
                return undefined;
            });
    }

    this.clearScreen = function(initial) {
        penData = [];
        if(initial) {
            return Promise.resolve();
        }
        if(wacomScreenButtonPressed) {
            //wacom will clear screenn by itself
            wacomScreenButtonPressed = false;
            continueCapthure();
            return Promise.resolve();
        }
        return Promise.resolve()
            .then(() => stopCapture())
            .then(() => api.send('StartCapture'))
            .then(() => {
                isCapturing = true;
                continueCapthure();
            });
    };

    this.close = function() {
        return Promise.resolve()
            .then(() => stopCapture())
            .finally(() => {
                if(api) {
                    api.shutdown();
                    api = null;
                }
            });
    };

    function getLastDown(_penData, index) {
        for(let i = index; i >= 0; i--) {
            if(_penData[i].isDown) {
                return i;
            }
        }
        return -1;
    }

    function getLastUp(_penData, index) {
        for(let i = index; i >= 0; i--) {
            if(!_penData[i].isDown) {
                return i;
            }
        }
        return -1;
    }

    this.makeSignatureData = function() {
        if(!penData || !penData.length)
            throw new Error(t('mouse.signature_not_collected'));
        if(wacomScreenButtonPressed) {
            //we need to remove last dot, which is "Ok" button press
            const lastTouchEnd = getLastDown(penData, penData.length - 1);
            const lastTouchStart = getLastUp(penData, lastTouchEnd);
            //capture also pen up events before button touch, don't know they are neede, just in case
            //old biosign did like this
            if(lastTouchStart <= 0)
                throw new Error(t('mouse.signature_not_collected'));
            // remove all items after lastDot + 1
            penData = penData.slice(0, lastTouchStart + 1); // include last "up"
        }

        const {
            tabletMaxX, tabletMaxY, tabletMaxPressure,
        } = usbDevice;

        const signatureData = {
            manufacturer: 'Wacom',
            // eslint-disable-next-line no-control-regex
            model: idProduct.replace(/^\u0001*/, '').replace(/\u0001*$/, ''),
            minPenTouchForce: 0,
            xPointMin: 0,
            yPointMin: 0,
            xPointMax: tabletMaxX,
            yPointMax: tabletMaxY,
            chx: null,
            chy: null,
            chpressure: null,
            userEnvironment,
        };

        // 100 lines per mm (= 2540 lpi)
        const xyPointScale = 100.0;
        // From an e-mail from Mr. Nick Mettyear (Wacom): "The maximum pen pressure level of 512 corresponds to a load of 200 g."
        const pressureScale = tabletMaxPressure / (0.2 * 9.81);
        const timeSampleRatePerSecond = 200.0; // 200 pps (points per second)
        const timeScalingValue = 1000.0; // milliseconds
        const timeMinValue = 0;
        const timeMaxValue = 65535;

        const chx = {
            scale: xyPointScale, min: 0, max: tabletMaxX, data: [],
        };
        const chy = {
            scale: xyPointScale, min: 0, max: tabletMaxY, data: [],
        };
        const chpressure = {
            scale: pressureScale, min: 0, max: tabletMaxPressure, data: [],
        };

        // plugin returns all zeros if timestamp counting is not enabled
        // but let's be careful and also handle strings, nulls, undefined and NaN
        let timeAllZero = true;
        let penTimeFirst;
        let chtime = {
            scale: timeScalingValue, min: timeMinValue, max: timeMaxValue, data: [],
        };

        for(let i = 0; i < penData.length; i++) {
            const penPoint = penData[i];
            chx.data.push(penPoint.x);
            chy.data.push(penPoint.y);
            chpressure.data.push(penPoint.pressure);
        }

        for(let i = 0; i < penData.length; i++) {
            const penPoint = penData[i];
            if((penPoint.time === null)
                || (penPoint.time === undefined)
                || ((typeof penPoint.time !== 'string') && (typeof penPoint.time !== 'number'))) {
                chtime = null;
                break;
            }
            let time = Number(penPoint.time);
            if(isNaN(time) || !isFinite(time)) {
                chtime = null;
                break;
            }
            // signature shoudl be at least 1 millisecond, isn't it?
            if(Math.round(time) != 0) timeAllZero = false;
            if(i == 0) {
                penTimeFirst = time;
            }
            time -= penTimeFirst;
            if(time > timeMaxValue)
                throw new Error(t('mouse.signature_duration_exceeded'));
            chtime.data.push(time);
        }

        if(timeAllZero) {
            chtime = null;
        }

        signatureData.chx = chx;
        signatureData.chy = chy;
        signatureData.chpressure = chpressure;
        if(chtime)
            signatureData.chtime = chtime;
        else
            signatureData.chtd = { scale: timeSampleRatePerSecond };

        return signatureData;
    };
}
